import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { EDITORIAL_STATUSES } from './editorial';
import ImageUpload from '../../components/ImageUpload';

const EMPTY = {
  title: '', ente: '', category: '', region: 'Nazionale', positions: '',
  publication_date: '', deadline: '', exam_date: '', status: 'APERTO',
  education_requirement: '', subjects: [], module_ids: [], price: 12.90, sales_enabled: false,
  cover_image: '', short_description: '', requirements: '',
  editorial_status: 'DRAFT', public_status: 'HIDDEN', available_from: '', featured: false, preview_section_ids: [],
  bando_analysis_id: null, official_source_ids: [],
};

export default function AdminContests() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [subjectsInput, setSubjectsInput] = useState('');
  const [previewInput, setPreviewInput] = useState('');

  const load = () => {
    api.get('/admin/contests').then(({ data }) => setItems(data.items)).catch(() => {});
    api.get('/admin/modules').then(({ data }) => setModules(data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setSubjectsInput(''); setPreviewInput(''); setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c.contest_id);
    setForm({ ...EMPTY, ...c });
    setSubjectsInput((c.subjects || []).join(', '));
    setPreviewInput((c.preview_section_ids || []).join(', '));
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      positions: form.positions === '' || form.positions == null ? null : Number(form.positions),
      price: Number(form.price),
      subjects: subjectsInput.split(',').map((s) => s.trim()).filter(Boolean),
      preview_section_ids: previewInput.split(',').map((s) => s.trim()).filter(Boolean),
      publication_date: form.publication_date || null,
      deadline: form.deadline || null,
      exam_date: form.exam_date || null,
      available_from: form.available_from || null,
    };
    try {
      if (editing) await api.put(`/admin/contests/${editing}`, payload);
      else await api.post('/admin/contests', payload);
      toast.success(editing ? 'Concorso aggiornato' : 'Concorso creato');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo concorso?')) return;
    await api.delete(`/admin/contests/${id}`);
    toast.success('Concorso eliminato');
    load();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/contests/${id}/status`, { editorial_status: status });
    toast.success(`Stato: ${status}`);
    load();
  };

  const publishCourse = async (id) => {
    try { const { data } = await api.get(`/admin/contests/${id}/course-readiness`); if (!data.ready) return toast.error(`Corso non pronto: ${data.blocking_reasons.join(', ')}`); await api.post(`/admin/contests/${id}/publish-course`); toast.success('Corso pubblicato e acquistabile'); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.code || e.response?.data?.detail || 'Pubblicazione non riuscita'); }
  };

  const F = (k) => ({ value: form[k] ?? '', onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div data-testid="admin-contests">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-semibold text-lg text-slate-900">Concorsi ({items.length})</h2>
        <Button onClick={openCreate} data-testid="contest-create" className="rounded-full bg-brand hover:bg-brand-hover text-white">
          <Plus size={15} strokeWidth={1.5} className="mr-1.5" /> Nuovo concorso
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {['Titolo', 'Ente', 'Prezzo', 'Stato editoriale', 'In evidenza', ''].map((h) => (
                <th key={h} className="px-5 py-3.5 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.contest_id} data-testid={`contest-row-${c.contest_id}`}>
                <td className="px-5 py-3.5 font-medium text-slate-800 max-w-[260px] truncate">{c.title}</td>
                <td className="px-5 py-3.5 text-slate-600">{c.ente}</td>
                <td className="px-5 py-3.5 text-slate-800 font-semibold">€ {Number(c.price).toFixed(2)}</td>
                <td className="px-5 py-3.5">
                  <Select value={c.editorial_status} onValueChange={(v) => setStatus(c.contest_id, v)}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`status-${c.contest_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EDITORIAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3.5">{c.featured ? <Badge className="bg-gold/10 text-gold border-gold/30 border">Sì</Badge> : '—'}</td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-1 justify-end">
                    {c.editorial_status === 'APPROVED' && <Button size="sm" variant="outline" onClick={() => publishCourse(c.contest_id)} data-testid={`publish-${c.contest_id}`}>Pubblica corso</Button>}
                    <Link className="p-2 text-brand text-sm" to={`/admin/preview/concorsi/${c.contest_id}`}>Anteprima privata</Link>
                    <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`edit-${c.contest_id}`}><Pencil size={15} strokeWidth={1.5} /></button>
                    <button onClick={() => remove(c.contest_id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`delete-${c.contest_id}`}><Trash2 size={15} strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Modifica concorso' : 'Nuovo concorso'}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="sm:col-span-2"><Label>Titolo</Label><Input className="mt-1.5" data-testid="form-title" {...F('title')} /></div>
            <div><Label>Ente</Label><Input className="mt-1.5" data-testid="form-ente" {...F('ente')} /></div>
            <div><Label>Categoria</Label><Input className="mt-1.5" placeholder="Enti locali, Ministeri..." data-testid="form-category" {...F('category')} /></div>
            <div><Label>Regione</Label><Input className="mt-1.5" {...F('region')} /></div>
            <div><Label>Posti (opzionale)</Label><Input type="number" min="1" className="mt-1.5" {...F('positions')} /></div>
            <div><Label>Data pubblicazione</Label><Input type="date" className="mt-1.5" {...F('publication_date')} /></div>
            <div><Label>Scadenza domanda</Label><Input type="date" className="mt-1.5" data-testid="form-deadline" {...F('deadline')} /></div>
            <div><Label>Data prova</Label><Input type="date" className="mt-1.5" {...F('exam_date')} /></div>
            <div><Label>Titolo di studio</Label><Input className="mt-1.5" placeholder="Diploma / Laurea" {...F('education_requirement')} /></div>
            <div>
              <Label>Stato bando</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['APERTO', 'IN_SCADENZA', 'CHIUSO'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Prezzo finale (€)</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="form-price" {...F('price')} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.sales_enabled === true} onCheckedChange={(v) => setForm({ ...form, sales_enabled: v === true })} />
              Abilita nuovi acquisti (richiede pubblicazione e switch globale aperto)
            </label>
            <div className="sm:col-span-2">
              <Label>Copertina</Label>
              <div className="mt-1.5 space-y-2">
                <ImageUpload value={form.cover_image} onUploaded={(url) => setForm({ ...form, cover_image: url })} label="Carica copertina" testId="cover-upload" />
                <Input placeholder="oppure incolla un URL immagine" data-testid="form-cover-url" {...F('cover_image')} />
              </div>
            </div>
            <div className="sm:col-span-2"><Label>Breve descrizione</Label><Textarea className="mt-1.5" rows={2} {...F('short_description')} /></div>
            <div className="sm:col-span-2"><Label>Requisiti</Label><Textarea className="mt-1.5" rows={2} {...F('requirements')} /></div>
            <div className="sm:col-span-2">
              <Label>Materie (separate da virgola)</Label>
              <Input className="mt-1.5" value={subjectsInput} onChange={(e) => setSubjectsInput(e.target.value)} placeholder="Diritto amministrativo, Diritto costituzionale" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-2 block">Moduli collegati (riutilizzabili tra concorsi)</Label>
              <div className="grid sm:grid-cols-2 gap-2 border border-slate-200 rounded-xl p-4 max-h-44 overflow-y-auto">
                {modules.map((m) => (
                  <label key={m.module_id} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox
                      checked={form.module_ids.includes(m.module_id)}
                      onCheckedChange={(checked) => setForm({
                        ...form,
                        module_ids: checked ? [...form.module_ids, m.module_id] : form.module_ids.filter((x) => x !== m.module_id),
                      })}
                      data-testid={`module-check-${m.module_id}`}
                    />
                    {m.title}
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Sezioni in anteprima gratuita (ID separati da virgola)</Label>
              <Input className="mt-1.5" value={previewInput} onChange={(e) => setPreviewInput(e.target.value)} placeholder="sec_xxx, sec_yyy" data-testid="form-preview" />
            </div>
            <div>
              <Label>Stato editoriale</Label>
              <Select value={form.editorial_status} onValueChange={(v) => setForm({ ...form, editorial_status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="form-editorial-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EDITORIAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visibilità pubblica</Label>
              <Select value={form.public_status} onValueChange={(v) => setForm({ ...form, public_status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="form-public-status"><SelectValue /></SelectTrigger>
                <SelectContent>{[['HIDDEN','Nascosto'],['COMING_SOON','In preparazione'],['PUBLISHED','Disponibile']].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Disponibile dal (facoltativo)</Label><Input type="date" className="mt-1.5" {...F('available_from')} /></div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} data-testid="form-featured" />
                In evidenza in home
              </label>
            </div>
          </div>
          <Button onClick={save} data-testid="form-save" className="mt-4 w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white">
            {editing ? 'Salva modifiche' : 'Crea concorso'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
